// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import type { PubmedPaperSummary, PubmedPaperDetails } from '../../../../../services/pubmedService';
import type { EvidenceItem, Scenario } from '../../../lib/models';
import { PaperListItem } from './PaperListItem';

export interface ScenarioTabProps {
  scenarios: Scenario[];
  expandedScenarios: Set<string>;
  setExpandedScenarios: React.Dispatch<React.SetStateAction<Set<string>>>;
  getCachedDetails: (pmid: string) => PubmedPaperDetails | undefined;
  libraryItems: EvidenceItem[];
  onPaperClick: (pmid: string, cachedDetails?: PubmedPaperDetails) => void;
  onDetach: (scenarioId: string, pmid: string) => void;
  selectedInputSource: string | null;
}

export const ScenarioTab: React.FC<ScenarioTabProps> = ({
  scenarios,
  expandedScenarios,
  setExpandedScenarios,
  getCachedDetails,
  libraryItems,
  onPaperClick,
  onDetach,
  selectedInputSource,
}) => {
  if (scenarios.length === 0) {
    return <div className="evidence-drawer__empty">No scenarios available</div>;
  }

  const toggleScenario = (scenarioId: string) => {
    setExpandedScenarios(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(scenarioId)) {
        newExpanded.delete(scenarioId);
      } else {
        newExpanded.add(scenarioId);
      }
      return newExpanded;
    });
  };

  return (
    <div className="evidence-drawer__scenario-container">
      <div className="evidence-drawer__scenario-list">
        {scenarios.map((scenario) => {
          const evidenceCount = scenario.evidenceIds?.length || 0;
          const evidenceItems = (scenario.evidenceIds || [])
            .map((pmid: string) => {
              const cached = getCachedDetails(pmid);
              const libraryItem = libraryItems.find((item) => item.id === pmid);
              return cached || libraryItem ? { pmid, cached, libraryItem } : null;
            })
            .filter((item: { pmid: string; cached: PubmedPaperDetails | undefined; libraryItem: EvidenceItem | undefined } | null): item is { pmid: string; cached: PubmedPaperDetails | undefined; libraryItem: EvidenceItem | undefined } => item !== null);

          const isExpanded = expandedScenarios.has(scenario.id);
          
          return (
            <div key={scenario.id} className="evidence-drawer__scenario-item">
              <div 
                className="evidence-drawer__scenario-header"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleScenario(scenario.id);
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleScenario(scenario.id);
                  }
                }}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} papers for ${scenario.name}`}
              >
                <h4 className="evidence-drawer__scenario-name" style={{ pointerEvents: 'none' }}>{scenario.name}</h4>
                <span className="evidence-drawer__scenario-count" style={{ pointerEvents: 'none' }}>
                  {evidenceCount} {evidenceCount === 1 ? 'paper' : 'papers'}
                  <span style={{ marginLeft: '8px', fontSize: '0.9em' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                </span>
              </div>
              {isExpanded && evidenceItems.length > 0 && (
                <div className="evidence-drawer__scenario-papers" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                  {evidenceItems.map(({ pmid, cached, libraryItem }: { pmid: string; cached: PubmedPaperDetails | undefined; libraryItem: EvidenceItem | undefined }) => {
                    const paperSummary: PubmedPaperSummary = cached ? {
                      pmid: cached.pmid,
                      title: cached.title,
                      journal: cached.journal,
                      year: cached.year,
                      firstAuthor: cached.authors?.[0] || libraryItem?.firstAuthor,
                      doi: cached.doi,
                      url: cached.url,
                    } : (libraryItem ? {
                      pmid: libraryItem.id,
                      title: libraryItem.title,
                      journal: libraryItem.journal,
                      year: libraryItem.year,
                      firstAuthor: libraryItem.firstAuthor,
                      doi: libraryItem.doi,
                      url: libraryItem.url,
                    } : {
                      pmid,
                      title: `PMID: ${pmid}`,
                      url: `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`,
                    });

                    return (
                      <div key={pmid} className="evidence-drawer__scenario-paper">
                        <PaperListItem
                          paper={paperSummary}
                          onClick={() => onPaperClick(pmid, cached)}
                          triageBrief={libraryItem?.aiBrief}
                          highlightInputSource={selectedInputSource}
                        />
                        <button
                          className="evidence-drawer__detach-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDetach(scenario.id, pmid);
                          }}
                          aria-label={`Detach ${paperSummary.title}`}
                        >
                          Detach
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
